import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Star, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeaveReviewModal = ({ open, onOpenChange, onSubmitted }) => {
  const { token } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const [formData, setFormData] = useState({
    role: "",
    company: "",
    content: "",
    rating: 5,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(`${API}/testimonials/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || "Avis soumis avec succès !");
        onOpenChange(false);
        setFormData({ role: "", company: "", content: "", rating: 5 });
        if (onSubmitted) onSubmitted();
      } else {
        toast.error(data.detail || "Erreur lors de l'envoi");
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Erreur réseau");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="leave-review-modal">
        <DialogHeader>
          <DialogTitle>Laisser un avis</DialogTitle>
          <DialogDescription>
            Votre avis sera publié après validation par l'administrateur.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Note</Label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: value })}
                  onMouseEnter={() => setHoverRating(value)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`rating-star-${value}`}
                >
                  <Star
                    className={`h-7 w-7 ${
                      value <= (hoverRating || formData.rating)
                        ? "fill-yellow-500 text-yellow-500"
                        : "text-muted-foreground"
                    }`}
                  />
                </button>
              ))}
              <span className="ml-2 text-sm text-muted-foreground">
                {formData.rating}/5
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-role">Votre rôle / fonction *</Label>
            <Input
              id="review-role"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              placeholder="ex: CTO, Développeur, Étudiant..."
              required
              minLength={2}
              maxLength={100}
              data-testid="review-role-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-company">Entreprise (optionnel)</Label>
            <Input
              id="review-company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              placeholder="Nom de votre entreprise"
              data-testid="review-company-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-content">Votre avis *</Label>
            <Textarea
              id="review-content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Partagez votre expérience..."
              rows={4}
              required
              minLength={10}
              maxLength={500}
              data-testid="review-content-input"
            />
            <p className="text-xs text-muted-foreground text-right">
              {formData.content.length}/500
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1"
              data-testid="submit-review-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                "Envoyer l'avis"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeaveReviewModal;
